import type { ReportLabel } from '@clockwise/clients/report-labels';
import {
  Folder,
  List,
  type LucideIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Share2,
  Star,
  Tag,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { LibraryView } from './library-model';

interface Props {
  view: LibraryView;
  /** Offer "Shared by you" — see `offersSharedByYou`. */
  sharedByYou: boolean;
  onView: (view: LibraryView) => void;
  counts: ReadonlyMap<LibraryView, number>;
  personal: readonly ReportLabel[];
  standard: readonly ReportLabel[];
  canManageStandard: boolean;
  onCreate: (kind: 'personal' | 'standard') => void;
  onRename: (label: ReportLabel) => void;
  onDelete: (label: ReportLabel) => void;
}

const FIXED: readonly { view: LibraryView; label: string; icon: LucideIcon }[] = [
  { view: 'all', label: 'All reports', icon: List },
  { view: 'starred', label: 'Starred', icon: Star },
  { view: 'mine', label: 'Created by you', icon: User },
  { view: 'shared-by-you', label: 'Shared by you', icon: Share2 },
  { view: 'shared', label: 'Shared with you', icon: Users },
];

/**
 * The left side of the Reports page: the fixed views, then YOUR labels, then
 * the standard ones — yours first because they are the ones you made to get
 * back to something.
 *
 * Every count follows the search, so the rail says where the matches are.
 */
export function LabelRail({
  view,
  sharedByYou,
  onView,
  counts,
  personal,
  standard,
  canManageStandard,
  onCreate,
  onRename,
  onDelete,
}: Props) {
  return (
    // A white panel card, like the filter bars on the other list pages, so the
    // rail reads as a surface beside the report card rather than loose rows on
    // the page. `self-start` keeps it the height of its rows instead of
    // stretching down the grid to match a long report list.
    <nav
      aria-label="Report views"
      data-surface="panel"
      className="space-y-1 self-start rounded-md border bg-card p-2 text-sm"
    >
      {FIXED.filter((f) => f.view !== 'shared-by-you' || sharedByYou).map((f) => (
        <RailItem
          key={f.view}
          icon={f.icon}
          name={f.label}
          active={view === f.view}
          count={counts.get(f.view) ?? 0}
          onClick={() => onView(f.view)}
        />
      ))}

      <SectionHeading title="Your labels" addLabel="New label" onAdd={() => onCreate('personal')} />
      {personal.map((l) => (
        <RailItem
          key={l.id}
          icon={Tag}
          name={l.name}
          active={view === `label:${l.id}`}
          count={counts.get(`label:${l.id}`) ?? 0}
          onClick={() => onView(`label:${l.id}`)}
          label={l}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}

      {(standard.length > 0 || canManageStandard) && (
        <>
          <SectionHeading
            title="Standard"
            addLabel={canManageStandard ? 'New standard label' : undefined}
            onAdd={canManageStandard ? () => onCreate('standard') : undefined}
          />
          {standard.map((l) => (
            <RailItem
              key={l.id}
              icon={Folder}
              name={l.name}
              active={view === `label:${l.id}`}
              count={counts.get(`label:${l.id}`) ?? 0}
              onClick={() => onView(`label:${l.id}`)}
              label={l}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </>
      )}
    </nav>
  );
}

function SectionHeading({
  title,
  addLabel,
  onAdd,
}: {
  title: string;
  addLabel?: string;
  onAdd?: () => void;
}) {
  return (
    <div className="mt-5 mb-1 flex h-6 items-center justify-between pl-2.5">
      <h2 className="font-medium text-muted-foreground text-xs">{title}</h2>
      {onAdd && addLabel ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-6 text-muted-foreground"
          aria-label={addLabel}
          onClick={onAdd}
        >
          <Plus className="size-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

function RailItem({
  icon: Icon,
  name,
  active,
  count,
  onClick,
  label,
  onRename,
  onDelete,
}: {
  icon: LucideIcon;
  name: string;
  active: boolean;
  count: number;
  onClick: () => void;
  label?: ReportLabel;
  onRename?: (label: ReportLabel) => void;
  onDelete?: (label: ReportLabel) => void;
}) {
  const manageable = label?.editable && onRename && onDelete;
  return (
    // `data-nav="item"` gives the row the main sidebar's (and the Setup
    // sub-nav's) geometry and lime-hover / blue-selected palette in light mode
    // — see `[data-nav='item']` in globals.css. That rule owns the padding, so
    // the button drops its own there; the classes below are the dark-mode look.
    <div
      data-nav="item"
      data-active={active}
      className={cn(
        'group/rail flex items-center rounded-md',
        active ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60',
      )}
    >
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        className="flex h-8 min-w-0 flex-1 items-center gap-2.5 px-2.5 text-left hover:text-foreground light:h-auto light:px-0 light:hover:text-inherit"
      >
        <Icon className="size-4 shrink-0 text-muted-foreground light:text-current" aria-hidden />
        <span className="truncate">{name}</span>
        <span
          className={cn(
            'ml-auto pl-2 text-muted-foreground text-xs tabular-nums',
            manageable && 'group-focus-within/rail:hidden group-hover/rail:hidden',
          )}
        >
          {count}
        </span>
      </button>
      {manageable ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="hidden size-6 shrink-0 group-focus-within/rail:inline-flex group-hover/rail:inline-flex data-[state=open]:inline-flex"
              aria-label={`Manage ${name}`}
            >
              <MoreHorizontal className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => onRename(label)}>
              <Pencil className="size-4" aria-hidden />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={() => onDelete(label)}>
              <Trash2 className="size-4" aria-hidden />
              Delete label
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
